import { AxiosResponse } from "axios";
import laravelApi from "./api";
import { CreateNetSuiteQuotePayload } from "@/models/netsuite/netsuite";
import {
  DriftReport,
  QuoteDraft,
  QuoteDraftPayload,
  SubmitQuoteDraftResponse
} from "@/models/quote_draft/quote_draft";

/**
 * FUPOF-191 — Quote drafts persisted in the Laravel API's PostgreSQL DB.
 * All endpoints live under /api/quotes (auth:sanctum). Drafts are shared across
 * a sales team: the API returns the authenticated user's quotes plus those of
 * every teammate that shares their team leader. Only a draft's creator (or an
 * admin) may delete it; any teammate may view, edit, and submit.
 */
export default class QuoteDraftService {
  /** List the caller's quotes and their whole sales team's (drafts and submitted). */
  public async listQuotes(): Promise<QuoteDraft[]> {
    try {
      const res: AxiosResponse = await laravelApi.get("/api/quotes");
      return res.data.quotes ?? res.data.data ?? res.data ?? [];
    } catch (err) {
      throw this.wrap(err, "Unknown error listing quote drafts.");
    }
  }

  public async createQuote(payload: QuoteDraftPayload): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post("/api/quotes", payload);
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error saving quote draft.");
    }
  }

  public async getQuote(id: number): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.get(`/api/quotes/${id}`);
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error loading quote draft.");
    }
  }

  /** Replace-all update of header + lines. */
  public async updateQuote(
    id: number,
    payload: QuoteDraftPayload
  ): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.put(
        `/api/quotes/${id}`,
        payload
      );
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error saving quote draft.");
    }
  }

  public async deleteQuote(id: number): Promise<void> {
    try {
      await laravelApi.delete(`/api/quotes/${id}`);
    } catch (err) {
      throw this.wrap(err, "Unknown error deleting quote draft.");
    }
  }

  /**
   * Submit the draft to NetSuite. The client builds the same
   * CreateNetSuiteQuotePayload shape used by the direct create_quote path, but
   * sends no custcol_* fields: the API owns line identity and injects the
   * NetSuite custom columns from PSQL itself. It forwards the payload to NS,
   * stores netsuite_quote_id / ns_line_ids, flips status to submitted, and
   * returns the persisted draft with those ids populated. When the draft
   * already has a netsuite_quote_id, the API updates the existing NS quote
   * instead of creating a new one.
   */
  public async submitQuote(
    id: number,
    payload: CreateNetSuiteQuotePayload,
    impersonatorName: string | null = null
  ): Promise<SubmitQuoteDraftResponse> {
    try {
      // FUPOF-222: named on the quote emails' origin line, as on requestSalesOrder.
      const body = impersonatorName
        ? { ...payload, impersonator_name: impersonatorName }
        : payload;
      const res: AxiosResponse = await laravelApi.post(
        `/api/quotes/${id}/submit`,
        body
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error submitting quote to NetSuite.");
    }
  }

  /**
   * Request that this quote be processed into a NetSuite Sales Order. Same
   * payload shape as {@link submitQuote}: the API create/updates the estimate
   * (auto-submit) AND flags the RESTlet to email the order desk, then flips the
   * draft to `pending_sales_order` (which locks it from further edits).
   */
  public async requestSalesOrder(
    id: number,
    payload: CreateNetSuiteQuotePayload,
    impersonatorName: string | null = null
  ): Promise<SubmitQuoteDraftResponse> {
    try {
      const body = impersonatorName
        ? { ...payload, impersonator_name: impersonatorName }
        : payload;
      const res: AxiosResponse = await laravelApi.post(
        `/api/quotes/${id}/request-sales-order`,
        body
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error requesting a sales order.");
    }
  }

  /** Re-fetch NS state for a submitted quote and return the drift report. */
  public async reloadQuote(id: number): Promise<DriftReport> {
    try {
      const res: AxiosResponse = await laravelApi.get(
        `/api/quotes/${id}/reload`
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error reloading quote from NetSuite.");
    }
  }

  /**
   * Adopt a pre-existing NS quote (no PSQL record) into PSQL for editing.
   * The API creates the quotes/quote_lines rows from NS state, with every
   * line best-effort SKU-parsed and flagged parsed: true.
   */
  public async adoptQuote(netsuiteQuoteId: number): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post("/api/quotes/adopt", {
        netsuite_quote_id: netsuiteQuoteId
      });
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error adopting NetSuite quote.");
    }
  }

  /**
   * Read-only sibling of {@link adoptQuote}: hydrates the same shape from a
   * pre-existing NS quote but persists nothing. The returned quote (and each of
   * its lines) comes back with id/created_at null by design — it's a transient
   * copy for the duplicate flow to seed the cart from, so no PSQL row is written
   * until the user saves the duplicate as its own draft.
   */
  public async previewAdoptQuote(
    netsuiteQuoteId: number
  ): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post(
        "/api/quotes/adopt/preview",
        { netsuite_quote_id: netsuiteQuoteId }
      );
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error previewing NetSuite quote.");
    }
  }

  private wrap(err: unknown, fallback: string): Error {
    const e = err as any;
    console.error("[QuoteDraftService]", e?.response?.data ?? e);
    return new Error(e?.response?.data?.message ?? e?.message ?? fallback);
  }
}
